<template>
	<div>
		<div class="columns email-input">
			<div class="column col-5">
				<input
					v-model="local"
					class="form-input"
					type="text"
					placeholder="아이디"
					maxlength="40"
					inputmode="email"
					autocapitalize="off"
					spellcheck="false"
					@input="emitValue"
				/>
			</div>
			<div class="column col-1 email-input__at">@</div>
			<div class="column col-6">
				<input
					v-model="domain"
					ref="domain"
					class="form-input"
					type="text"
					:placeholder="domainPlaceholder"
					:readonly="isDomainReadOnly"
					maxlength="30"
					inputmode="email"
					autocapitalize="off"
					spellcheck="false"
					@input="changeDomain"
					@mousedown="clickDomain"
				/>
			</div>
			<div class="column col-12">
				<select
					v-model="domainSelect"
					ref="select"
					class="form-select"
					@change="changeDomainSelect"
				>
					<option value="">선택</option>
					<option v-for="item in domains" :key="item" :value="item">
						{{ item }}
					</option>
					<option :value="other">직접입력</option>
				</select>
			</div>
		</div>

		<div v-if="hint" class="form-input-hint">
			<i class="ri-information-line"></i>
			{{ hint }}
		</div>

		<div v-if="suggestion" class="form-input-hint email-input__suggestion">
			<i class="ri-information-line"></i>
			혹시 {{ suggestion }} 아닌가요?
			<button
				type="button"
				class="email-input__suggestion-button"
				@click="applySuggestion"
			>
				바꾸기
			</button>
		</div>
	</div>
</template>

<script>
import {
	EMAIL_DOMAINS,
	EMAIL_DOMAIN_OTHER,
	splitEmail,
	findSimilarDomain,
} from '@/utils/email';

/**
 * 이메일을 아이디 / @ / 도메인 세 칸으로 나눠 입력받는다.
 *
 * 부모에는 합쳐진 주소 하나만 올려보내므로 <EmailInput v-model="userEmail" /> 로 쓴다.
 * 도메인을 목록에서 고르면 오타가 아예 생길 수 없고, 직접 친 경우에는
 * 흔한 도메인과 한두 글자만 다를 때 '혹시 ~ 아닌가요?' 로 알려준다.
 */
export default {
	props: {
		value: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			local: '',
			domain: '',
			domainSelect: '',

			// 아직 도메인을 고르지 않았거나 목록에서 고른 상태면 직접 칠 수 없다
			isDomainReadOnly: true,

			// 목록이 저절로 안 펼쳐지는 브라우저에서 대신 보여줄 안내
			hint: '',

			// 오타로 보일 때 제안할 도메인
			suggestion: '',

			domains: EMAIL_DOMAINS,
			other: EMAIL_DOMAIN_OTHER,
		};
	},
	computed: {
		joined() {
			const local = this.local.trim();
			const domain = this.domain.trim();

			return local && domain ? `${local}@${domain}` : '';
		},
		domainPlaceholder() {
			return this.isDomainReadOnly ? '주소 선택' : '직접입력';
		},
	},
	watch: {
		value: {
			immediate: true,
			handler(val) {
				// 우리가 올려보낸 값이 그대로 돌아온 것이면 다시 쪼갤 필요가 없다
				if ((val || '') === this.joined) return;

				this.applyValue(val);
			},
		},
	},
	methods: {
		/** 부모가 넘겨준 주소를 두 칸에 나눠 담는다 (회원정보 수정에서 저장된 값을 불러올 때) */
		applyValue(value) {
			const { local, domain } = splitEmail(value);

			this.local = local;
			this.domain = domain;

			this.syncSelectFromDomain();
			this.hint = '';
			this.suggestion = '';
		},

		/** 도메인 칸의 값에 맞춰 셀렉트를 골라 준다 */
		syncSelectFromDomain() {
			const domain = this.domain.trim().toLowerCase();

			if (!domain) {
				// 아직 아무것도 고르지 않은 상태
				this.domainSelect = '';
				this.isDomainReadOnly = true;
			} else if (this.domains.includes(domain)) {
				this.domainSelect = domain;
				this.isDomainReadOnly = true;
			} else {
				// 목록에 없는 회사 메일 등
				this.domainSelect = EMAIL_DOMAIN_OTHER;
				this.isDomainReadOnly = false;
			}
		},

		/** 셀렉트를 바꿨을 때 */
		changeDomainSelect() {
			if (this.domainSelect === '') {
				this.domain = '';
				this.isDomainReadOnly = true;
			} else if (this.domainSelect === EMAIL_DOMAIN_OTHER) {
				this.domain = '';
				this.isDomainReadOnly = false;
				this.$nextTick(() => this.$refs.domain.focus());
			} else {
				this.domain = this.domainSelect;
				this.isDomainReadOnly = true;
			}

			this.hint = '';
			this.suggestion = '';
			this.emitValue();
		},

		/** 도메인을 직접 쳤을 때 */
		changeDomain() {
			this.updateSuggestion();
			this.emitValue();
		},

		/**
		 * 잠긴 도메인 칸을 눌렀을 때. 커서를 넣는 대신 셀렉트를 펼쳐 준다.
		 *
		 * showPicker() 는 비교적 최근에 생긴 기능이라 없는 브라우저가 있다. 그때는
		 * 셀렉트로 옮겨만 주고 말로 안내한다. 단, 이미 주소가 채워져 있으면 안내하지
		 * 않는다. 다 고른 사람에게 고르라고 하면 오히려 헷갈린다.
		 */
		clickDomain(event) {
			if (!this.isDomainReadOnly) return;

			// 잠긴 칸에 커서가 들어가지 않게. 휴대폰 키보드가 헛되이 올라오는 것도 막힌다.
			event.preventDefault();

			try {
				this.$refs.select.showPicker();
				this.hint = '';
			} catch (error) {
				this.$refs.select.focus();

				if (!this.domain.trim()) {
					this.hint = '목록에서 이메일 주소를 선택해 주세요.';
				}
			}
		},

		/** 직접 친 도메인이 오타로 보이면 제안을 띄운다 */
		updateSuggestion() {
			const domain = this.domain.trim().toLowerCase();

			if (!domain || this.domains.includes(domain)) {
				this.suggestion = '';
				return;
			}

			this.suggestion = findSimilarDomain(domain);
		},

		applySuggestion() {
			this.domain = this.suggestion;

			this.syncSelectFromDomain();
			this.suggestion = '';
			this.emitValue();
		},

		emitValue() {
			this.$emit('input', this.joined);
		},
	},
};
</script>

<style scoped>
.email-input {
	align-items: center;
}

.email-input__at {
	color: #666;
	text-align: center;
}

.email-input__suggestion {
	color: #f53838;
}

.email-input__suggestion-button {
	padding: 0;
	border: 0;
	background: none;
	color: inherit;
	font-size: inherit;
	text-decoration: underline;
	cursor: pointer;
}

/* 잠긴 도메인 칸은 누르면 셀렉트가 열리므로 눌러지는 칸으로 보이게 한다 */
.email-input .form-input:read-only {
	background-color: #f8f9fa;
	cursor: pointer;
}
</style>
